import { useEffect, useMemo } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { QUERY_FETCH_RESOURCE_HUB_BY_SLUG } from '@edwin-group/people-graphql';
import { useResourceHubDetailStore } from '../store/useResourceHubDetailStore';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';

interface ResourceData {
  documentId: string;
  title: string;
  slug: string;
  briefDescription?: string | null;
  subHeader?: string | null;
  publicationDate?: string | null;
  resourceStatus?: 'New' | 'Updated' | null;
  expireResourceStatusAt?: string | null;
  type?: {
    documentId: string;
    type: string;
  } | null;
  category?: {
    documentId: string;
    category: string;
  } | null;
  cover?: {
    url?: string | null;
    alternativeText?: string | null;
    mime?: string | null;
  } | null;
  resourceHistory?: Array<{
    documentId: string;
    version: string | number;
    createdAt?: string | null;
  }> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  DefaultContentTemplate?: any[] | null;
}

export const useResourceHubDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const setResource = useResourceHubDetailStore((state) => state.setResource);
  const resource = useResourceHubDetailStore((state) => state.resource);

  const { data: dataBySlug } = useSuspenseQuery<{
    resources_connection?: { nodes?: ResourceData[] | null } | null;
  }>(QUERY_FETCH_RESOURCE_HUB_BY_SLUG, {
    variables: { slug: slug || '' },
    skip: !slug,
    fetchPolicy: 'cache-and-network',
  });

  const resourceData: ResourceData | null | undefined = dataBySlug?.resources_connection?.nodes?.[0] || null;

  useEffect(() => {
    if (resourceData) {
      const res = resourceData;
      setResource({
        documentId: res.documentId,
        title: res.title,
        slug: res.slug,
        briefDescription: res.briefDescription,
        subHeader: res.subHeader,
        publicationDate: res.publicationDate,
        resourceStatus: res.resourceStatus,
        expireResourceStatusAt: res.expireResourceStatusAt,
        type: res.type,
        category: res.category,
        cover: res.cover,
        resourceHistory: res.resourceHistory?.map((h) => ({
          documentId: h.documentId,
          version: h.version,
          createdAt: h.createdAt,
        })),
        DefaultContentTemplate: res.DefaultContentTemplate,
      });

      const openedResourceVersion = [...(res.resourceHistory || [])]?.sort(
        (a, b) => Number(b.version) - Number(a.version),
      )?.[0]?.version;
      trackGAEvent('resource_opened', {
        resource_name: res.title,
        resource_type: res.type?.type ?? '',
        resource_id: res.documentId,
        resource_category: res.category?.category ?? '',
        resource_version: openedResourceVersion ? Number(openedResourceVersion) : 0,
      });
    }
  }, [resourceData, setResource]);

  const currentVersion = useMemo(() => {
    if (!resource?.resourceHistory?.length) return 1;

    const sortedHistory = [...resource.resourceHistory]
      .filter((h) => h.createdAt)
      .sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime());

    return sortedHistory[0]?.version || 1;
  }, [resource?.resourceHistory]);

  return { resource, currentVersion };
};
